import React, { useEffect, useState } from "react";
import {
  Choices,
  Flow,
  Plan,
  Risks,
  Rules,
  Stack,
  StepsStrip,
  WHO,
} from "@/components/site/AuditParts";

// The written Audit: one document, a page to a question. The sample on the
// public site and every client's Audit are drawn by this file from the same
// shape of data (see SAMPLE_DOC in lib/sampleAudit.js). A page whose data is
// missing is left out and the rest are numbered without it: an Audit that
// says "do not build" has no page for the tool.

const two = (n) => String(n).padStart(2, "0");
const some = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v));
// A row of up to three or four boxes, as wide as there are boxes.
const COLS = {
  1: "",
  2: "sm:grid-cols-2 print:grid-cols-2",
  3: "sm:grid-cols-3 print:grid-cols-3",
  4: "sm:grid-cols-4 print:grid-cols-4",
};

export const MARKS = {
  counted: "Counted",
  estimated: "Estimated",
  planned: "Planned",
  decided: "Decided",
};
// Where a figure came from. Four marks, one look each.
const MARK_STYLE = {
  counted: "bg-black text-white",
  estimated: "ring-1 ring-black/40",
  planned: "border border-dashed border-black/40",
  decided: "bg-black/[0.07]",
};
function Mark({ kind }) {
  if (!MARKS[kind]) return null;
  return (
    <span
      className={`inline-flex h-5 items-center rounded-full px-2 text-[10px] font-medium uppercase tracking-[0.06em] ${MARK_STYLE[kind]}`}
    >
      {MARKS[kind]}
    </span>
  );
}
const WHO_STYLE = {
  tool: "bg-black text-white",
  approve: "ring-1 ring-black/40",
  person: "bg-black/[0.07]",
  open: "text-black/50 ring-1 ring-black/15",
};
const Label = ({ children, className = "" }) => (
  <p
    className={`text-[12px] uppercase tracking-[0.08em] text-black/45 ${className}`}
  >
    {children}
  </p>
);
const Numbered = ({ lines }) => (
  <ol className="mt-3 space-y-2.5">
    {lines.map((line, i) => (
      <li key={line} className="flex gap-4 text-[16px] leading-[1.4]">
        <span className="w-5 shrink-0 text-[12px] tabular-nums text-black/35">
          {two(i + 1)}
        </span>
        {line}
      </li>
    ))}
  </ol>
);

function Answer({ doc }) {
  const a = doc.answer;
  return (
    <>
      <p className="nm-display text-[52px] leading-[0.95] tracking-[-0.03em] sm:text-[84px]">
        {a.verdict}
      </p>
      {a.text && (
        <p className="mt-6 max-w-[620px] text-[18px] leading-[1.4] text-black/70 sm:text-[20px]">
          {a.text}
        </p>
      )}
      {some(a.figures) && (
        <dl
          className={`mt-10 grid gap-px overflow-hidden rounded-[14px] bg-black/10 ${COLS[a.figures.length]}`}
        >
          {a.figures.map((f) => (
            <div key={f.label} className="bg-[#f7f7f7] p-6">
              <dd className="nm-display text-[40px] leading-none tracking-[-0.03em] sm:text-[46px]">
                {f.value}
              </dd>
              <dd className="mt-1 text-[13px] text-black/50">{f.unit}</dd>
              <dt className="mt-5 text-[14px] leading-[1.35]">{f.label}</dt>
              <dd className="mt-3">
                <Mark kind={f.mark} />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {some(a.scale) && (
        <dl
          className={`mt-3 grid gap-px overflow-hidden rounded-[14px] bg-black/10 ${COLS[a.scale.length]}`}
        >
          {a.scale.map(([value, label]) => (
            <div key={label} className="bg-black p-6 text-white">
              <dd className="nm-display text-[32px] leading-none tracking-[-0.03em] sm:text-[36px]">
                {value}
              </dd>
              <dt className="mt-2 text-[14px] text-white/65">{label}</dt>
            </div>
          ))}
        </dl>
      )}
      {a.scaleNote && (
        <p className="mt-3 text-[13px] text-black/50">{a.scaleNote}</p>
      )}
    </>
  );
}

function Scope({ doc }) {
  const s = doc.scope;
  return (
    <>
      {s.sentence && (
        <>
          <Label>The job, in one sentence</Label>
          <p className="mt-3 max-w-[680px] text-[20px] leading-[1.35] tracking-[-0.015em] sm:text-[24px]">
            {s.sentence}
          </p>
        </>
      )}
      {some(s.facts) && (
        <dl className="mt-10 border-t border-black/10 print:mt-6">
          {s.facts.map(([k, v, mark]) => (
            <div
              key={k}
              className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-black/10 py-3.5 print:py-2.5"
            >
              <dt className="text-[15px] text-black/55">{k}</dt>
              <dd className="flex items-center gap-3 text-[16px]">
                {v} <Mark kind={mark} />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {some(s.plan?.rows) && (
        <>
          <Label className="mt-10 print:mt-7">
            {s.plan.title || "The plan"}
          </Label>
          <div className="mt-3 flex items-start gap-3">
            <div className="flex-1">
              <Plan rows={s.plan.rows} note={s.plan.note} />
            </div>
            <Mark kind={s.plan.mark || "planned"} />
          </div>
        </>
      )}
      {some(s.method) && (
        <>
          <Label className="mt-10 print:mt-7">How we measured</Label>
          <Numbered lines={s.method} />
        </>
      )}
      <div className="mt-8 flex flex-wrap gap-2 print:mt-5">
        {Object.keys(MARKS).map((k) => (
          <Mark key={k} kind={k} />
        ))}
      </div>
    </>
  );
}

function Steps({ doc }) {
  const [view, setView] = useState("before");
  const { list, before = "Before", after = "With the tool" } = doc.steps;
  const tab = (id, label) => (
    <button
      type="button"
      onClick={() => setView(id)}
      aria-pressed={view === id}
      data-testid={`steps-${id}`}
      className={`h-9 rounded-full px-5 text-[13px] font-medium transition-colors ${view === id ? "bg-black text-white" : "text-black/60 hover:text-black"}`}
    >
      {label}
    </button>
  );
  return (
    <>
      <div className="inline-flex rounded-full bg-[#f7f7f7] p-1 print:hidden">
        {tab("before", before)}
        {tab("after", after)}
      </div>
      <StepsStrip steps={list} view={view} />
      <ol className="mt-6 border-t border-black/10 print:mt-0">
        {list.map((s, i) => (
          <li
            key={s.name}
            className="grid break-inside-avoid gap-x-6 gap-y-1 border-b border-black/10 py-4 sm:grid-cols-12 print:grid-cols-12 print:py-2.5"
          >
            <span className="text-[12px] tabular-nums text-black/35 sm:col-span-1 sm:pt-1 print:col-span-1">
              {two(i + 1)}
            </span>
            <p className="text-[17px] leading-[1.25] tracking-[-0.01em] sm:col-span-4 print:col-span-4 print:text-[14px]">
              {s.name}
            </p>
            <div className="sm:col-span-7 print:col-span-7">
              <p
                className={`text-[15px] leading-[1.45] text-black/65 print:block print:text-[12.5px] ${view === "before" ? "" : "hidden"}`}
              >
                <span className="hidden font-medium text-black print:inline">
                  Before:{" "}
                </span>
                {s.before}
              </p>
              <div
                className={`print:mt-1 print:block ${view === "after" ? "" : "hidden"}`}
              >
                <p className="text-[15px] leading-[1.45] text-black/65 print:text-[12.5px]">
                  <span className="hidden font-medium text-black print:inline">
                    With the tool:{" "}
                  </span>
                  {s.after}
                </p>
                {WHO[s.who] && (
                  <span
                    className={`mt-2 inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-medium print:mt-1 print:h-5 ${WHO_STYLE[s.who]}`}
                  >
                    {WHO[s.who]}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}

function Leaks({ doc }) {
  return (
    <ol className="border-t border-black/10">
      {doc.leaks.map((l, i) => (
        <li
          key={l.name}
          className="grid break-inside-avoid gap-x-6 gap-y-2 border-b border-black/10 py-6 sm:grid-cols-12 print:grid-cols-12 print:py-4"
        >
          <div className="sm:col-span-4 print:col-span-4">
            <p className="nm-display text-[34px] leading-none tracking-[-0.03em] print:text-[26px]">
              {l.size}
            </p>
            <div className="mt-3">
              <Mark kind={l.mark} />
            </div>
          </div>
          <div className="sm:col-span-8 print:col-span-8">
            <p className="text-[19px] leading-[1.2] tracking-[-0.015em]">
              <span className="mr-3 text-[12px] tabular-nums text-black/35">
                {two(i + 1)}
              </span>
              {l.name}
            </p>
            <p className="mt-2 text-[15px] leading-[1.5] text-black/60">
              {l.text}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function Foundations({ doc }) {
  const total = doc.foundations.reduce((s, f) => s + f.points, 0);
  return (
    <>
      <p className="nm-display text-[52px] leading-none tracking-[-0.03em]">
        {total}
        <span className="text-black/30"> of {doc.foundations.length * 3}</span>
      </p>
      <ul className="mt-8 border-t border-black/10">
        {doc.foundations.map((f) => (
          <li
            key={f.area}
            className="grid break-inside-avoid gap-x-6 gap-y-2 border-b border-black/10 py-5 sm:grid-cols-12 print:grid-cols-12"
          >
            <div className="sm:col-span-4 print:col-span-4">
              <p className="text-[16px]">{f.area}</p>
              <span className="mt-2 flex max-w-[140px] gap-1">
                {[0, 1, 2].map((s) => (
                  <span
                    key={s}
                    className={`h-2 flex-1 rounded-full ${f.points > s ? "bg-black" : "bg-black/10"}`}
                  />
                ))}
              </span>
            </div>
            <p className="text-[15px] leading-[1.5] text-black/60 sm:col-span-8 print:col-span-8">
              {f.text}
            </p>
          </li>
        ))}
      </ul>
    </>
  );
}

function Software({ doc }) {
  return (
    <ul className="border-t border-black/10">
      {doc.software.map((s) => (
        <li
          key={s.system}
          className="grid break-inside-avoid gap-x-6 gap-y-2 border-b border-black/10 py-5 sm:grid-cols-12 print:grid-cols-12"
        >
          <div className="sm:col-span-3 print:col-span-3">
            <p className="text-[16px]">{s.system}</p>
            <p className="text-[13px] text-black/45">{s.vendor}</p>
          </div>
          <p className="text-[15px] leading-[1.5] text-black/65 sm:col-span-6 print:col-span-6">
            {s.allows}
          </p>
          {s.rule && (
            <p className="text-[13px] leading-[1.4] sm:col-span-3 print:col-span-3">
              <span className="block text-[10px] uppercase tracking-[0.08em] text-black/40">
                Our rule
              </span>
              {s.rule}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function Tool({ doc }) {
  const t = doc.tool;
  return (
    <>
      {some(t.flows) && <Flow flows={t.flows} />}
      {some(t.lists) && (
        <div
          className={`grid gap-px overflow-hidden rounded-[14px] bg-black/10 sm:grid-cols-2 print:grid-cols-2 ${some(t.flows) ? "mt-10" : ""}`}
        >
          {t.lists.map((col) => (
            <div key={col.head} className="bg-[#f7f7f7] p-6 print:p-4">
              <p className="text-[18px] tracking-[-0.015em]">{col.head}</p>
              <ul className="mt-4 space-y-2 print:mt-2 print:space-y-1">
                {col.items.map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-[15px] leading-[1.4] text-black/65"
                  >
                    <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-black/40" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {some(t.never) && (
        <div className="mt-3 break-inside-avoid rounded-[14px] bg-black p-6 text-white">
          <p className="text-[18px] tracking-[-0.015em]">It never</p>
          <ul className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2 print:grid-cols-2">
            {t.never.map((item) => (
              <li
                key={item}
                className="text-[15px] leading-[1.4] text-white/75"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {some(t.rules) && <Rules rules={t.rules} />}
    </>
  );
}

function Honest({ doc }) {
  const h = doc.honest;
  return (
    <>
      {some(h.notBuilt) && (
        <ul className="border-t border-black/10">
          {h.notBuilt.map(([name, text]) => (
            <li
              key={name}
              className="grid break-inside-avoid gap-x-6 gap-y-1 border-b border-black/10 py-5 sm:grid-cols-12 print:grid-cols-12"
            >
              <p className="text-[17px] tracking-[-0.01em] sm:col-span-4 print:col-span-4">
                {name}
              </p>
              <p className="text-[15px] leading-[1.5] text-black/60 sm:col-span-8 print:col-span-8">
                {text}
              </p>
            </li>
          ))}
        </ul>
      )}
      {some(h.fix) && (
        <>
          <h3
            className={`nm-title text-[24px] leading-[1.05] sm:text-[30px] ${some(h.notBuilt) ? "mt-12" : ""}`}
          >
            What you can fix this week, without us
          </h3>
          <div className="mt-2">
            <Numbered lines={h.fix} />
          </div>
        </>
      )}
    </>
  );
}

function Price({ doc }) {
  const p = doc.price;
  return (
    <>
      {p.tool && (
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <p className="nm-display text-[72px] leading-[0.9] tracking-[-0.04em] sm:text-[110px] print:text-[84px]">
            {p.tool}
          </p>
          <p className="pb-2 text-[15px] leading-[1.4] text-black/55">
            for the tool, fixed.
            {p.care && (
              <>
                <br />
                Care: {p.care.charAt(0).toLowerCase() + p.care.slice(1)}.
              </>
            )}
          </p>
        </div>
      )}
      {some(p.includes) && (
        <ul className="mt-10 grid gap-x-8 border-t border-black/10 sm:grid-cols-2 print:grid-cols-2">
          {p.includes.map((line) => (
            <li
              key={line}
              className="border-b border-black/10 py-3.5 text-[15px] leading-[1.4]"
            >
              {line}
            </li>
          ))}
        </ul>
      )}
      {p.held && (
        <p className="mt-6 text-[15px] text-black/60">
          This price is held until <span className="text-black">{p.held}</span>.
        </p>
      )}
      {p.no && (
        <p
          className={`rounded-[14px] bg-[#f7f7f7] p-6 text-[16px] leading-[1.45] ${p.tool || some(p.includes) ? "mt-8" : ""}`}
        >
          {p.no}
        </p>
      )}
    </>
  );
}

function Proof({ doc }) {
  const d = doc.proof;
  const list = (label, lines) =>
    some(lines) && (
      <>
        <Label className="mt-10 print:mt-7">{label}</Label>
        <ul className="mt-3 space-y-3">
          {lines.map((line) => (
            <li
              key={line}
              className="border-l border-black/15 pl-4 text-[16px] leading-[1.45] text-black/70"
            >
              {line}
            </li>
          ))}
        </ul>
      </>
    );
  return (
    <>
      {d.intro && (
        <p className="mb-8 max-w-[680px] text-[18px] leading-[1.4] text-black/70 sm:text-[20px]">
          {d.intro}
        </p>
      )}
      {some(d.figures) && (
        <dl
          className={`grid gap-px overflow-hidden rounded-[14px] bg-black/10 ${d.figures.length === 4 ? "grid-cols-2" : ""} ${COLS[d.figures.length]}`}
        >
          {d.figures.map(([value, label, mark]) => (
            <div key={label} className="bg-[#f7f7f7] p-5">
              <dd className="nm-display text-[40px] leading-none tracking-[-0.03em]">
                {value}
              </dd>
              <dt className="mt-3 text-[13px] leading-[1.3]">{label}</dt>
              <dd className="mt-3">
                <Mark kind={mark} />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {list("What testing found", d.found)}
      {list("Still unknown", d.open)}
      {d.test && (
        <p className="mt-10 break-inside-avoid rounded-[14px] bg-black p-6 text-[17px] leading-[1.45] text-white">
          {d.test}
        </p>
      )}
      {doc.meta?.asOf && (
        <p className="mt-4 text-[13px] text-black/45">
          Figures as of {doc.meta.asOf}.
        </p>
      )}
    </>
  );
}

// The pages, in order: id, title, what must be there for the page to exist,
// what draws it, and how to read it.
const ORDER = [
  [
    "answer",
    "The answer",
    (d) => d.answer?.verdict,
    Answer,
    "Every Audit opens with the answer. If you read one page, read this one. The rest is how we got there.",
  ],
  [
    "scope",
    "What we measured",
    (d) => d.scope && (d.scope.sentence || some(d.scope.facts)),
    Scope,
    "Each number says where it came from: counted, estimated, planned or decided. Trust the counted ones most.",
  ],
  [
    "steps",
    "The job, step by step",
    (d) => some(d.steps?.list),
    Steps,
    "Your own job, written down step by step, with what each step waits for. Switch between before and with the tool.",
  ],
  [
    "leaks",
    "Where it leaked",
    (d) => some(d.leaks),
    Leaks,
    "Ranked by what it costs, not by what is easiest to fix.",
  ],
  [
    "foundations",
    "Foundations",
    (d) => some(d.foundations),
    Foundations,
    "The same five areas as the two-minute check, scored on evidence instead of answers. A tool is only built on what holds.",
  ],
  [
    "software",
    "What the software already allowed",
    (d) => some(d.software),
    Software,
    "Before proposing anything, we check what the software you already pay for can do. Often it is more than the team knows.",
  ],
  [
    "tool",
    "The tool",
    (d) => d.tool && (some(d.tool.flows) || some(d.tool.lists)),
    Tool,
    "The tool as a line, then as lists, then the rules it runs under. If something is not on this page, the tool does not do it.",
  ],
  [
    "choices",
    "Why this, and not that",
    (d) => some(d.choices),
    ({ doc }) => <Choices list={doc.choices} />,
    "Every tool is a set of choices. You should be able to see each one, what we turned down, and what it cost.",
  ],
  [
    "stack",
    "What it runs on",
    (d) => some(d.stack),
    ({ doc }) => <Stack layers={doc.stack} />,
    "Named, so you can check it. The models will change. What you own is the layer they read: the rules, the log and the record.",
  ],
  [
    "risks",
    "What could go wrong",
    (d) => some(d.risks),
    ({ doc }) => <Risks list={doc.risks} />,
    "If we cannot say how it fails, we do not understand it yet. Each risk has the one thing that holds it.",
  ],
  [
    "honest",
    "What we would not build",
    (d) => d.honest && (some(d.honest.notBuilt) || some(d.honest.fix)),
    Honest,
    "Two lists that cost us work: what we refused to build, and what you can fix this week without us.",
  ],
  [
    "price",
    "The price",
    (d) => d.price && (d.price.tool || d.price.no),
    Price,
    "One number, fixed before you decide.",
  ],
  [
    "proof",
    "The proof",
    (d) => d.proof && (d.proof.intro || some(d.proof.figures) || d.proof.test),
    Proof,
    "Most audits end in a recommendation. Ours ends with the thing running: a first version doing one real case of yours.",
  ],
];

// The pages a document has. `extra` adds pages that are not part of the
// stored document, each placed after the page it names:
// { after, id, title, note, body, screenOnly }. A page that only works on a
// screen is left off paper.
export function docPages(doc, extra = []) {
  const pages = [];
  for (const [id, title, has, Body, note] of ORDER) {
    if (has(doc))
      pages.push({
        id,
        title:
          id === "honest" && !some(doc.honest?.notBuilt)
            ? "What you can fix yourself"
            : title,
        note: doc.notes?.[id] || note,
        body: <Body doc={doc} />,
      });
    for (const page of extra) if (page.after === id) pages.push(page);
  }
  return pages;
}

// One page of the document: a sheet of paper with a running head, the page
// itself, a note in the margin on how to read it, and a page number.
function Sheet({ doc, page, n, of }) {
  return (
    <article
      id={page.id}
      data-sheet={page.id}
      className={`${page.screenOnly ? "print:hidden" : "nm-sheet"} scroll-mt-24 rounded-[10px] bg-white shadow-[0_1px_0_rgba(0,0,0,0.04),0_24px_60px_-40px_rgba(0,0,0,0.35)] print:rounded-none print:shadow-none`}
    >
      <header className="flex items-center justify-between gap-6 border-b border-black/10 px-6 py-4 text-[11px] uppercase tracking-[0.08em] text-black/45 sm:px-10 print:px-0 print:py-3">
        <span>
          Nomus · Audit{doc.meta?.number ? ` ${doc.meta.number}` : ""}
          {doc.meta?.job ? ` · ${doc.meta.job}` : ""}
        </span>
        <span className="shrink-0 tabular-nums">
          {two(n)} / {two(of)}
        </span>
      </header>
      <div className="grid gap-8 px-6 py-8 sm:px-10 sm:py-12 xl:grid-cols-12 print:block print:px-0 print:py-6">
        <div className="xl:col-span-8">
          <h2 className="nm-title text-[30px] leading-[1.02] sm:text-[44px] print:text-[38px]">
            {page.title}
          </h2>
          {/* On paper the note sits under the title, and the page has the
              full width of the sheet. */}
          {page.note && (
            <p className="mt-3 hidden max-w-[720px] border-l border-black/15 pl-4 text-[14px] leading-[1.45] text-black/55 print:block">
              {page.note}
            </p>
          )}
          <div className="mt-8 print:mt-6">{page.body}</div>
        </div>
        {page.note && (
          <aside className="xl:col-span-3 xl:col-start-10 print:hidden">
            <div className="border-l border-black/15 pl-4 xl:sticky xl:top-28">
              <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-black/40">
                How to read this page
              </p>
              <p className="mt-2 text-[14px] leading-[1.45] text-black/60">
                {page.note}
              </p>
            </div>
          </aside>
        )}
      </div>
    </article>
  );
}

// The first sheet of paper when it is printed: whose Audit, of what, when.
function Cover({ doc, client }) {
  const m = doc.meta || {};
  return (
    <div className="nm-sheet hidden min-h-[240mm] flex-col justify-between print:flex">
      <img
        src="/brand/nomus-logo-email.png"
        alt="Nomus"
        width="150"
        height="24"
        className="h-6 w-auto self-start"
      />
      <div>
        <p className="text-[12px] uppercase tracking-[0.1em] text-black/45">
          Audit{m.number ? ` ${m.number}` : ""}
        </p>
        <p className="nm-title mt-4 text-[64px] leading-[0.98]">
          {m.job || "Your Audit"}
        </p>
        {doc.answer?.verdict && (
          <p className="nm-display mt-6 text-[30px] leading-none tracking-[-0.02em] text-black/45">
            {doc.answer.verdict}
          </p>
        )}
      </div>
      <dl className="grid grid-cols-4 gap-x-8 border-t border-black/15 pt-5 text-[12px]">
        {[
          ["Prepared for", client || m.company],
          ["When", m.period],
          ["Prepared by", m.by],
          ["Figures as of", m.asOf],
        ]
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k}>
              <dt className="text-black/45">{k}</dt>
              <dd className="mt-1">{v}</dd>
            </div>
          ))}
      </dl>
    </div>
  );
}

// The whole document: a list of its pages that follows the reader, and the
// pages themselves. On paper: a cover, then a page to a sheet.
export default function AuditDoc({ doc, extra, client }) {
  const pages = docPages(doc, extra);
  const [active, setActive] = useState(pages[0]?.id);

  // The contents list follows the page being read.
  useEffect(() => {
    const on = () => {
      const line = window.innerHeight * 0.35;
      let current = null;
      document.querySelectorAll("[data-sheet]").forEach((el) => {
        if (current === null) current = el.dataset.sheet;
        if (el.getBoundingClientRect().top <= line) current = el.dataset.sheet;
      });
      if (current) setActive(current);
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <section
      className="nm-doc bg-[#ececec] px-3 py-8 sm:px-5 lg:py-14 print:bg-white print:p-0"
      data-testid="audit-doc"
    >
      <div className="mx-auto max-w-[1400px] lg:grid lg:grid-cols-12 lg:gap-8 print:block">
        <nav
          aria-label="Pages"
          className="hidden lg:col-span-2 lg:block print:hidden"
        >
          <ol className="sticky top-28 space-y-1">
            {pages.map((page, i) => (
              <li key={page.id}>
                <a
                  href={`#${page.id}`}
                  className={`flex gap-3 rounded-[8px] px-3 py-2 text-[13px] leading-[1.3] transition-colors ${active === page.id ? "bg-white text-black" : "text-black/50 hover:text-black"}`}
                >
                  <span className="tabular-nums text-black/35">
                    {two(i + 1)}
                  </span>
                  {page.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>
        <div className="space-y-4 sm:space-y-6 lg:col-span-10 print:space-y-0">
          <Cover doc={doc} client={client} />
          {pages.map((page, i) => (
            <Sheet
              key={page.id}
              doc={doc}
              page={page}
              n={i + 1}
              of={pages.length}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
